import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError, descargarRecurso } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard, fmtNum } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Lock, Unlock, Ban, CheckCircle2, AlertTriangle, Tag, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import QuickMaestroSelect from "@/components/QuickMaestroSelect";

const ESTADO_META = {
  disponible: { label: "Disponible", cls: "bg-[#10B981] hover:bg-[#10B981]" },
  agotado: { label: "Agotado", cls: "bg-[#94A3B8] hover:bg-[#94A3B8]" },
  bloqueado: { label: "Bloqueado", cls: "bg-[#EF4444] hover:bg-[#EF4444]" },
  retirado: { label: "Retirado", cls: "bg-[#0E2A47] hover:bg-[#0E2A47]" },
};
const REC_EMPTY = {
  materia_prima_id: "", proveedor_id: "", cantidad: "", unidad: "", fecha_recepcion: "",
  lote_proveedor: "", codigo_lote: "", conforme: "si", motivo_no_conforme: "",
};

export default function Lotes() {
  const { user, scopeTenant } = useAuth();
  const canEdit = ["superadmin", "admin", "calidad", "operario"].includes(user.rol);
  const canBloquear = ["superadmin", "admin", "calidad"].includes(user.rol);
  const needsScope = useScopeGuard(user, scopeTenant);
  const nav = useNavigate();

  const [lotes, setLotes] = useState([]);
  const [recepciones, setRecepciones] = useState([]);
  const [mp, setMp] = useState([]);
  const [prov, setProv] = useState([]);

  const [recOpen, setRecOpen] = useState(false);
  const [rec, setRec] = useState(REC_EMPTY);
  const [motivoDlg, setMotivoDlg] = useState(null); // { accion, lote }
  const [motivo, setMotivo] = useState("");
  const [cascada, setCascada] = useState(null); // { lote, data }
  const [etiqDlg, setEtiqDlg] = useState(null); // { lote }
  const [etq, setEtq] = useState({ formato: "a4_8", copias: 1, campos_extra: [] });
  const [impresiones, setImpresiones] = useState([]);

  const load = useCallback(async () => {
    if (needsScope) return;
    const [l, r, m, p] = await Promise.all([
      api.get("/lotes"), api.get("/recepciones"), api.get("/materias-primas"), api.get("/proveedores"),
    ]);
    setLotes(l.data); setRecepciones(r.data); setMp(m.data); setProv(p.data);
  }, [needsScope]);
  useEffect(() => { load(); }, [load]);

  const crearRec = async () => {
    if (!rec.materia_prima_id) return toast.error("Seleccione la materia prima");
    if (!rec.cantidad || Number(rec.cantidad) <= 0) return toast.error("Indique una cantidad válida");
    if (rec.conforme === "no" && !rec.motivo_no_conforme.trim()) return toast.error("Indique el motivo de la no conformidad");
    const payload = {
      materia_prima_id: rec.materia_prima_id,
      proveedor_id: rec.proveedor_id || null,
      cantidad: Number(rec.cantidad),
      unidad: rec.unidad || null,
      fecha_recepcion: rec.fecha_recepcion || null,
      lote_proveedor: rec.lote_proveedor || null,
      codigo_lote: rec.codigo_lote || null,
      conforme: rec.conforme === "si",
      motivo_no_conforme: rec.conforme === "no" ? rec.motivo_no_conforme : null,
    };
    try {
      const { data } = await api.post("/recepciones", payload);
      toast.success(`Recepción registrada · lote ${data.lote.codigo} creado`);
      setRecOpen(false); setRec(REC_EMPTY); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const abrirMotivo = (accion, lote) => { setMotivoDlg({ accion, lote }); setMotivo(""); };
  const confirmarMotivo = async () => {
    const { accion, lote } = motivoDlg;
    if (!motivo.trim()) return toast.error("El motivo es obligatorio");
    try {
      const { data } = await api.post(`/lotes/${lote.id}/${accion}`, { motivo });
      toast.success(accion === "bloquear" ? "Lote bloqueado" : "Lote retirado");
      setMotivoDlg(null); load();
      const c = data.cascada;
      if (c && (c.lotes_pt_afectados.length || c.expediciones_afectadas.length)) setCascada({ lote: data.lote, data: c });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const bloquearPT = async (pt) => {
    try {
      const { data } = await api.post(`/lotes/${pt.id}/bloquear`, { motivo: `Cascada: contiene lote ${cascada.lote.codigo} bloqueado` });
      toast.success(`Lote PT ${pt.codigo} bloqueado`); load();
      const c = data.cascada;
      if (c && c.expediciones_afectadas.length) setCascada({ lote: data.lote, data: c });
      else setCascada({ ...cascada, data: { ...cascada.data, lotes_pt_afectados: cascada.data.lotes_pt_afectados.filter((x) => x.id !== pt.id) } });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const desbloquear = async (lote) => {
    try { await api.post(`/lotes/${lote.id}/desbloquear`); toast.success("Lote desbloqueado"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const abrirEtiqueta = async (lote) => {
    setEtiqDlg({ lote }); setEtq({ formato: "a4_8", copias: 1, campos_extra: [] });
    const { data } = await api.get("/etiquetas/impresiones", { params: { lote_id: lote.id } });
    setImpresiones(data);
  };
  const generarEtiqueta = async () => {
    try {
      const { data } = await api.post("/etiquetas/generar", {
        modo: "vinculada", lote_id: etiqDlg.lote.id, formato: etq.formato, copias: Number(etq.copias),
        campos_extra: etq.campos_extra.filter((c) => c.etiqueta || c.valor),
      });
      toast.success("Etiquetas generadas"); descargarRecurso("etiqueta", data.id);
      const r = await api.get("/etiquetas/impresiones", { params: { lote_id: etiqDlg.lote.id } });
      setImpresiones(r.data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (needsScope) return <ScopeNotice />;

  const provNombre = (id) => prov.find((p) => p.id === id)?.nombre || "—";
  const mpNombre = (id) => mp.find((m) => m.id === id)?.nombre || "—";

  return (
    <div data-testid="lotes-page">
      <PageHeader overline="Nivel 3 · Traza" title="Recepciones y Lotes"
        action={canEdit && <Button variant="outline" onClick={() => nav("/etiquetas")} data-testid="etiqueta-manual-btn"><Tag size={16} className="mr-1" />Etiqueta manual</Button>} />

      <Tabs defaultValue="lotes">
        <TabsList className="bg-white border border-[#E2E8F0]">
          <TabsTrigger value="lotes" data-testid="tab-lotes">Lotes</TabsTrigger>
          <TabsTrigger value="recepciones" data-testid="tab-recepciones">Recepciones</TabsTrigger>
        </TabsList>

        {/* Lotes */}
        <TabsContent value="lotes">
          <div className="flex justify-end mb-3">
            {canEdit && <Button onClick={() => { setRec(REC_EMPTY); setRecOpen(true); }} data-testid="nueva-recepcion-btn" className="bg-[#FF5625] hover:bg-[#E0451A]"><Plus size={16} className="mr-1" />Recepción</Button>}
          </div>
          {lotes.length === 0 ? (
            <div className="text-[#0E2A47]/40 py-12 text-center">Aún no hay lotes. Registre una recepción para crear el primero.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lotes.map((l) => <LoteCard key={l.id} l={l} provNombre={provNombre} canBloquear={canBloquear} canEdit={canEdit}
                onBloquear={() => abrirMotivo("bloquear", l)} onRetirar={() => abrirMotivo("retirar", l)} onDesbloquear={() => desbloquear(l)} onEtiqueta={() => abrirEtiqueta(l)} />)}
            </div>
          )}
        </TabsContent>

        {/* Recepciones */}
        <TabsContent value="recepciones">
          <Card className="border-[#E2E8F0] mt-3">
            <Table data-testid="tabla-recepciones">
              <TableHeader><TableRow>
                <TableHead>Fecha</TableHead><TableHead>Materia prima</TableHead><TableHead>Proveedor</TableHead>
                <TableHead>Cantidad</TableHead><TableHead>Lote</TableHead><TableHead>Conforme</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {recepciones.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#0E2A47]/40 py-8">Sin recepciones</TableCell></TableRow>}
                {recepciones.map((r) => (
                  <TableRow key={r.id} data-testid={`recepcion-${r.id}`}>
                    <TableCell>{new Date(r.fecha_recepcion).toLocaleDateString("es-ES")}</TableCell>
                    <TableCell className="font-medium text-[#0E2A47]">{r.materia_prima_nombre}</TableCell>
                    <TableCell>{provNombre(r.proveedor_id)}</TableCell>
                    <TableCell>{r.cantidad} {r.unidad}</TableCell>
                    <TableCell><Badge variant="outline" className="border-[#0E2A47]/20 font-mono">{r.lote_codigo}</Badge></TableCell>
                    <TableCell>{r.conforme
                      ? <span className="text-[#10B981] flex items-center gap-1 text-sm"><CheckCircle2 size={14} />Sí</span>
                      : <span className="text-[#EF4444] flex items-center gap-1 text-sm" title={r.motivo_no_conforme}><AlertTriangle size={14} />No</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog recepción */}
      <Dialog open={recOpen} onOpenChange={setRecOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="recepcion-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">Nueva recepción</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Fld label="Materia prima">
              <QuickMaestroSelect tipo="materias-primas" value={rec.materia_prima_id}
                onChange={(v) => setRec({ ...rec, materia_prima_id: v })} items={mp}
                onCreated={(item) => setMp((prev) => [...prev, item])}
                proveedores={prov} onProveedorCreated={(p) => setProv((prev) => [...prev, p])}
                testid="rec-materia-prima" placeholder="Seleccione…" />
            </Fld>
            <div className="grid grid-cols-2 gap-4">
              <Fld label="Cantidad"><Input type="number" min="0" step="any" value={rec.cantidad} onChange={(e) => setRec({ ...rec, cantidad: e.target.value })} data-testid="rec-cantidad" className="mt-1.5" /></Fld>
              <Fld label="Unidad">
                <Select value={rec.unidad || "kg"} onValueChange={(v) => setRec({ ...rec, unidad: v })}>
                  <SelectTrigger data-testid="rec-unidad" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="kg">kg</SelectItem><SelectItem value="g">g</SelectItem><SelectItem value="l">l</SelectItem><SelectItem value="ud">ud</SelectItem></SelectContent>
                </Select>
              </Fld>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Fld label="Fecha recepción"><Input type="date" value={rec.fecha_recepcion} onChange={(e) => setRec({ ...rec, fecha_recepcion: e.target.value })} data-testid="rec-fecha" className="mt-1.5" /></Fld>
              <Fld label="Código lote (auto)"><Input value={rec.codigo_lote} onChange={(e) => setRec({ ...rec, codigo_lote: e.target.value })} data-testid="rec-codigo-lote" placeholder="AAMMDD-NN" className="mt-1.5 font-mono" /></Fld>
            </div>
            <Fld label="Lote del proveedor"><Input value={rec.lote_proveedor} onChange={(e) => setRec({ ...rec, lote_proveedor: e.target.value })} data-testid="rec-lote-prov" className="mt-1.5" /></Fld>
            <Fld label="¿Conforme?">
              <Select value={rec.conforme} onValueChange={(v) => setRec({ ...rec, conforme: v })}>
                <SelectTrigger data-testid="rec-conforme" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="si">Sí, conforme</SelectItem><SelectItem value="no">No conforme (nace bloqueado)</SelectItem></SelectContent>
              </Select>
            </Fld>
            {rec.conforme === "no" && (
              <Fld label="Motivo de la no conformidad">
                <Textarea value={rec.motivo_no_conforme} onChange={(e) => setRec({ ...rec, motivo_no_conforme: e.target.value })} data-testid="rec-motivo" className="mt-1.5" />
              </Fld>
            )}
          </div>
          <DialogFooter><Button onClick={crearRec} data-testid="rec-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Registrar recepción</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog motivo (bloquear / retirar) */}
      <Dialog open={!!motivoDlg} onOpenChange={(o) => !o && setMotivoDlg(null)}>
        <DialogContent className="bg-white" data-testid="motivo-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">{motivoDlg?.accion === "bloquear" ? "Bloquear lote" : "Retirar lote"} {motivoDlg?.lote.codigo}</DialogTitle></DialogHeader>
          <Fld label="Motivo (obligatorio)"><Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="motivo-input" className="mt-1.5" /></Fld>
          <DialogFooter><Button onClick={confirmarMotivo} data-testid="motivo-submit" className="bg-[#EF4444] hover:bg-[#dc2626]">Confirmar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog etiquetas del lote */}
      <Dialog open={!!etiqDlg} onOpenChange={(o) => !o && setEtiqDlg(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="etiqueta-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">Etiquetas · {etiqDlg?.lote.codigo}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Formato</Label>
                <Select value={etq.formato} onValueChange={(v) => setEtq({ ...etq, formato: v })}>
                  <SelectTrigger data-testid="etq-formato" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="a4_8">8 por A4 (105×74mm)</SelectItem><SelectItem value="individual">Individual (100×60mm)</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Copias</Label>
                <Input type="number" min="1" max="200" value={etq.copias} onChange={(e) => setEtq({ ...etq, copias: e.target.value })} data-testid="etq-copias" className="mt-1.5" /></div>
            </div>
            {etiqDlg && ["bloqueado", "retirado"].includes(etiqDlg.lote.estado) && (
              <div className="text-xs text-[#B91C1C] bg-[#FEF2F2] border border-[#EF4444]/30 rounded p-2">La etiqueta incluirá la franja "{etiqDlg.lote.estado === "bloqueado" ? "BLOQUEADO" : "RETIRADO"} — NO USAR".</div>
            )}
            <Button onClick={generarEtiqueta} data-testid="etq-generar" className="w-full bg-[#FF5625] hover:bg-[#E0451A]"><Printer size={15} className="mr-1" />Generar e imprimir PDF</Button>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60 mb-2">Etiquetas impresas ({impresiones.length})</div>
              <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="etiquetas-impresas">
                {impresiones.length === 0 && <div className="text-xs text-[#0E2A47]/40">Aún no se han impreso etiquetas de este lote.</div>}
                {impresiones.map((i) => (
                  <div key={i.id} className="flex items-center justify-between text-xs border-b border-[#E2E8F0] py-1.5" data-testid={`impresion-${i.id}`}>
                    <span>{i.copias} ud · {i.formato === "a4_8" ? "A4×8" : "individual"} · {i.usuario_nombre}</span>
                    <span className="flex items-center gap-2 text-[#0E2A47]/50">{new Date(i.creado_en).toLocaleString("es-ES")}
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => descargarRecurso("etiqueta", i.id)} data-testid={`impresion-descargar-${i.id}`}><Printer size={12} /></Button></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Dialog cascada de bloqueo */}
      <Dialog open={!!cascada} onOpenChange={(o) => !o && setCascada(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="cascada-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47] flex items-center gap-2">
            {cascada?.data.expediciones_afectadas.length ? <><AlertTriangle size={18} className="text-[#EF4444]" />Producto ya expedido</> : <>Bloqueo en cascada</>}
          </DialogTitle></DialogHeader>
          {cascada && (
            <div className="space-y-4">
              {cascada.data.lotes_pt_afectados.length > 0 && (
                <div data-testid="cascada-pt">
                  <p className="text-sm text-[#0E2A47]/80 mb-2">El lote <b>{cascada.lote.codigo}</b> se ha consumido en estos lotes de producto terminado. Se propone bloquearlos también:</p>
                  <div className="space-y-2">
                    {cascada.data.lotes_pt_afectados.map((pt) => (
                      <div key={pt.id} className="flex items-center justify-between border border-[#E2E8F0] rounded-md px-3 py-2" data-testid={`cascada-pt-${pt.id}`}>
                        <div>
                          <span className="font-mono font-semibold text-[#0E2A47]">{pt.codigo}</span>
                          <span className="text-xs text-[#0E2A47]/60 ml-2">{pt.producto_nombre} · disp. {fmtNum(pt.disponible)} · expedido {fmtNum(pt.expedida)}</span>
                        </div>
                        {pt.estado === "bloqueado"
                          ? <Badge className="bg-[#EF4444] hover:bg-[#EF4444] text-[10px] text-white">Bloqueado</Badge>
                          : <Button size="sm" className="h-7 text-xs bg-[#EF4444] hover:bg-[#dc2626]" onClick={() => bloquearPT(pt)} data-testid={`bloquear-pt-${pt.id}`}><Lock size={12} className="mr-1" />Bloquear PT</Button>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {cascada.data.expediciones_afectadas.length > 0 && (
                <div className="bg-[#FEF2F2] border border-[#EF4444]/30 rounded-md p-3" data-testid="cascada-expediciones">
                  <p className="text-sm font-semibold text-[#B91C1C] mb-2 flex items-center gap-1"><AlertTriangle size={15} />Este lote ya se ha expedido. Preámbulo de una retirada real:</p>
                  <div className="space-y-1.5">
                    {cascada.data.expediciones_afectadas.map((e) => (
                      <div key={e.id} className="text-xs text-[#0E2A47] flex flex-wrap gap-x-3" data-testid={`cascada-exp-${e.id}`}>
                        <span className="font-mono font-semibold">{e.codigo}</span>
                        <span><b>Cliente:</b> {e.cliente}</span>
                        {e.albaran && <span><b>Albarán:</b> {e.albaran}</span>}
                        <span><b>Cantidad:</b> {fmtNum(e.cantidad)}</span>
                        <span><b>Fecha:</b> {new Date(e.fecha).toLocaleDateString("es-ES")}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#B91C1C]/80 mt-2">Se ha generado una alerta y un evento a Núcleo (lote_bloqueado).</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setCascada(null)} data-testid="cascada-cerrar">Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LoteCard({ l, provNombre, canBloquear, canEdit, onBloquear, onRetirar, onDesbloquear, onEtiqueta }) {
  const meta = ESTADO_META[l.estado] || ESTADO_META.disponible;
  const barra = (label, val, color) => (
    <div className="flex items-center justify-between text-xs py-0.5">
      <span className="text-[#0E2A47]/60">{label}</span>
      <span className={`font-mono font-semibold ${color}`}>{fmtNum(val)} {l.unidad}</span>
    </div>
  );
  return (
    <Card className="p-4 border-[#E2E8F0]" data-testid={`lote-${l.id}`} data-verify-balance-lote-cuadra={l.balance_cuadra}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-mono font-black text-lg text-[#0E2A47] tracking-tight">{l.codigo}</div>
          <div className="text-xs text-[#0E2A47]/60">{l.materia_prima_nombre || "—"} · {provNombre(l.proveedor_id)}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge className={`text-[10px] text-white ${meta.cls}`} data-testid={`lote-estado-${l.id}`}>{meta.label}</Badge>
          <Badge variant="outline" className="text-[10px] border-[#0E2A47]/20">{l.tipo === "mp" ? "Materia prima" : "Producto"}</Badge>
        </div>
      </div>

      <div className="bg-[#F7F8FA] rounded-md p-3 mt-2">
        {barra("Inicial", l.cantidad_inicial, "text-[#0E2A47]")}
        {barra("Disponible", l.disponible, "text-[#10B981]")}
        {barra("Consumido", l.consumida, "text-[#3B82F6]")}
        {barra("Expedido", l.expedida, "text-[#8B5CF6]")}
        {barra("Mermas", l.mermas, "text-[#F59E0B]")}
        <div className="border-t border-[#E2E8F0] mt-2 pt-2 flex items-center justify-between text-xs">
          <span className="text-[#0E2A47]/60">Balance</span>
          {l.balance_cuadra
            ? <span className="text-[#10B981] font-semibold flex items-center gap-1" data-testid={`lote-balance-ok-${l.id}`}><CheckCircle2 size={13} />Cuadra</span>
            : <span className="text-[#EF4444] font-semibold flex items-center gap-1" data-testid={`lote-balance-ko-${l.id}`}><AlertTriangle size={13} />No cuadra</span>}
        </div>
      </div>

      {l.motivo_bloqueo && <div className="text-xs text-[#EF4444] mt-2">Motivo: {l.motivo_bloqueo}</div>}

      {canBloquear && l.estado !== "retirado" && (
        <div className="flex flex-wrap gap-1 mt-3">
          {l.estado === "bloqueado"
            ? <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onDesbloquear} data-testid={`desbloquear-${l.id}`}><Unlock size={12} className="mr-1" />Desbloquear</Button>
            : <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onBloquear} data-testid={`bloquear-${l.id}`}><Lock size={12} className="mr-1" />Bloquear</Button>}
          <Button size="sm" variant="ghost" className="h-7 text-xs text-[#0E2A47]" onClick={onRetirar} data-testid={`retirar-${l.id}`}><Ban size={12} className="mr-1" />Retirar</Button>
        </div>
      )}
      {canEdit && (
        <Button size="sm" variant="outline" className="h-7 text-xs mt-2 w-full" onClick={onEtiqueta} data-testid={`etiqueta-${l.id}`}><Printer size={12} className="mr-1" />Imprimir etiquetas</Button>
      )}
    </Card>
  );
}

function Fld({ label, children }) {
  return (<div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">{label}</Label>{children}</div>);
}
